import React, {ReactElement, useEffect, useState} from 'react'
import {
    Grid,
    GridCellProps,
    GridColumn as Column,
    GridDataStateChangeEvent,
    GridNoRecords,
    GridToolbar
} from "@progress/kendo-react-grid";
import {process} from "@progress/kendo-data-query";
import {useTranslation} from "react-i18next";
import {Box, IconButton} from '@mui/material';
import {Cached} from "@mui/icons-material";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from '@hookform/resolvers/yup';
import {CustomButton, GridFieldActionsCell} from "ndr-designsystem"
import GridDataState from "../../api/fixed/GridDateState";
import {useAppDispatch, useAppSelector} from "../../app/hooks"
import {AddAdminUser, DeleteAdminUser, fetchAdminUsers, fetchUsers} from "./store/Thunks"
import {setAdmins} from './store/AdminsSlice';
import AdminUsersValidation from '../../validations/AdminUsersValidation';
// eslint-disable-next-line camelcase
import type {IGetUsersByGroupGroupsUsers as AdminUser} from '../../api/generated/models';
import useStyles from './AdminsListStyle';
import {setCurrentPage} from "../appStateSlice";
import AddDialog from "../../Components/AddDialog/AddDialog";

const AdminsList = (): ReactElement => {
    const classes = useStyles()
    const [currentAdmin, setCurrentAdmin] = useState<AdminUser | undefined>(undefined);
    const { admins, users } = useAppSelector(state => state.Admins);
    const { t } = useTranslation(['tabels', 'common']);
    const dispatch = useAppDispatch();
    const { control, reset, formState, handleSubmit: HandleAdminDialogSubmit }
        = useForm<any>({
            mode: 'onTouched',
            resolver: yupResolver(AdminUsersValidation()),
        });
    const [dataState, setDataState] = useState<GridDataState>({
        skip: 0,
        take: 10,
        filter: undefined,
        sort: []
    });

    useEffect(() => {
        dispatch(fetchAdminUsers())
        dispatch(fetchUsers())
        dispatch(setCurrentPage("admins_list"))

        return () => {
            dispatch(setAdmins(undefined))
        }
    }, [dispatch, t])

    const forceRefresh = (): void => {
        dispatch(setAdmins(undefined))
        dispatch(fetchAdminUsers())
    }

    const onDataChange = (changeEvent: GridDataStateChangeEvent): void => {
        setDataState(changeEvent.dataState);
    }

    const onAdminDialogSubmit: SubmitHandler<any> = async (data) => {
        await dispatch(AddAdminUser(data.userId));
        dispatch(fetchAdminUsers());
        dispatch(fetchUsers());
        setCurrentAdmin(undefined);
        reset();
    }

    const onCurrentAdminClose = (): void => {
        setCurrentAdmin(undefined)
        reset()
    }

    const handleRemove = async (adminIndex: number): Promise<void> => {
        await dispatch(DeleteAdminUser((admins ?? [])[adminIndex]))
        dispatch(fetchAdminUsers())
        dispatch(fetchUsers())
    }

    const CommandsCell = (props: GridCellProps): ReactElement =>
        <GridFieldActionsCell {...props} remove={handleRemove} />

    return (
        <div>
            <AddDialog
                onClose={onCurrentAdminClose}
                currentData={currentAdmin}
                onSubmit={HandleAdminDialogSubmit(onAdminDialogSubmit)}
                control={control}
                formState={formState}
                users={users ?? []}
            />
            <Grid className="no-scrollbar" data={process(admins ?? [], dataState)} {...dataState} onDataStateChange={onDataChange} dataItemKey="id" sortable pageable>
                <GridToolbar>
                    <div className={classes.addNewRightButton}>
                        <IconButton color="primary" component="span" onClick={forceRefresh}>
                            <Cached color="primary"/>
                        </IconButton>
                        <CustomButton title={t('tenantsList.add_new')}
                                      onClick={() => {
                                          setCurrentAdmin({})
                                      }}/>
                    </div>
                </GridToolbar>
                <GridNoRecords>
                    {admins === undefined ?
                        "" : t('tabels:no_records_text')}
                </GridNoRecords>
                <Column field="username" title={t('adminsList.name')} />
                <Column field="eMail" title={t('adminsList.email')} />
                <Column field="isActive" title={t('adminsList.active')} />
                <Column cell={CommandsCell} />
            </Grid>
        </div>
    )
}


export default AdminsList
